import { cloneElement, type KeyboardEvent, type ReactElement, type ReactNode, useCallback, useId, useState } from 'react';

export interface TooltipProps {
  /** The tip's text. Keep it short; it is a name or a one-line hint, never the only place an instruction lives. */
  readonly content: ReactNode;
  /** The trigger: ONE element that can take focus (a button, a link, an input). It receives `aria-describedby`. */
  readonly children: ReactElement<{ 'aria-describedby'?: string }>;
  /** Where the tip sits: above the trigger (default) or below it. 8px from the trigger either way. */
  readonly placement?: 'top' | 'bottom';
}

/**
 * The tooltip (Design System): an inverse bubble, `6px 10px`, radius 8, 12.5/500, a small shadow, 8px from its trigger. It shows
 * on hover AND on keyboard focus of the trigger, stays while the pointer is on the bubble (WCAG 1.4.13 hoverable) and Escape
 * dismisses it without moving focus. The trigger is described by it through `aria-describedby`; the bubble is `role="tooltip"`.
 * A control that already has a visible name keeps it — the tooltip adds, never replaces.
 */
export function Tooltip({ content, children, placement = 'top' }: TooltipProps): ReactElement {
  const id = useId();
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const open = (hover || focus) && !dismissed;
  const onKeyDown = useCallback((e: KeyboardEvent<HTMLSpanElement>): void => {
    if (e.key === 'Escape') setDismissed(true);
  }, []);
  const described = [children.props['aria-describedby'], id].filter((v): v is string => v !== undefined).join(' ');
  return (
    <span
      className="vello-tooltip-host"
      onMouseEnter={() => {
        setHover(true);
        setDismissed(false);
      }}
      onMouseLeave={() => setHover(false)}
      onFocus={() => {
        setFocus(true);
        setDismissed(false);
      }}
      onBlur={() => setFocus(false)}
      onKeyDown={onKeyDown}
    >
      {cloneElement(children, { 'aria-describedby': described })}
      <span id={id} role="tooltip" className="vello-tooltip" data-placement={placement} data-open={open ? 'true' : 'false'}>
        <span className="vello-tooltip__bubble">{content}</span>
      </span>
    </span>
  );
}
